import {shortcodeToUnified} from '../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {readApiModel} from '../../../helpers/apiHelpers';
import {teammateHasReactionOnComment, toggleReactionOnComment} from '../../../helpers/models/commentHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {ApiCommentReactionError} from '../../../models/api/commentReactionModel';
import {ApiCommentFromReactionDataGroup} from '../../../models/api/dataGroupModel';
import {deleteApiAsync, postApiAsync} from '../../../services/http/http';
import {HttpError, HttpResponseError} from '../../../services/http/httpError';
import {findCommentLock} from '../../../services/locks/resourceLocks';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {updateDataAction} from '../dataGroupsActions';
import {getOriginalData} from '../dataSelectors';
import {DataState} from '../dataState';
import {addCommentReactionErrorAction} from '../localPreferences/localPreferencesActions';

/*
 * Action creators.
 */

export function toggleReactionOnCommentAsyncAction(
  commentUid: string,
  shortcode: string,
  shouldKeep?: boolean,
): ThunkAsyncAction {
  const emojiString = shortcodeToUnified(shortcode);

  return async (dispatch, getState) => {
    const {userId} = getOriginalData(getState());

    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'comments'>(
      'comments',
      createObjectPropertyUpdater(
        'byId',
        createResourceMapValueUpdater(
          commentUid,
          createUpdater<CommentModel>((comment) =>
            toggleReactionOnComment(comment, userId, emojiString, shouldKeep),
          ),
        ),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCommentLock(commentUid).lockAsync(async () => {
          // Find the current state of the comment.
          const state = getState();
          const {comments} = getOriginalData(state);
          const comment = comments.byId[commentUid];
          if (!comment) {
            return undefined;
          }

          // Figure out what to do.
          const hasReaction = teammateHasReactionOnComment(comment, userId, emojiString);
          const shouldHaveReaction = shouldKeep ? true : !hasReaction;
          trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_COMMENT_REACT, {
            action: shouldHaveReaction ? 'add' : 'remove',
            type: emojiString,
            conversation_id: comment.conversationId,
          });
          if (hasReaction === shouldHaveReaction) {
            return undefined;
          }

          try {
            // Perform the API request.
            const path = `${comment.url}/reaction`;
            const body = {
              reaction: emojiString,
            };
            const dataGroup = shouldHaveReaction
              ? await postApiAsync(ApiCommentFromReactionDataGroup, path, body)
              : await deleteApiAsync(ApiCommentFromReactionDataGroup, path, body);

            // Finish the update.
            return updateDataAction(dataGroup);
          } catch (error) {
            // Handle API error responses.
            if (error instanceof HttpResponseError) {
              try {
                const apiError = readApiModel(ApiCommentReactionError, error.body);

                // Show reaction error banner.
                dispatch(addCommentReactionErrorAction(apiError.type));
                return undefined;
              } catch {
                // Fall through.
              }
            }

            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}
